// Pre-Approval milestones — Section 1 (Applications). Two attestation
// rows (Borrower + Seller). Each row tracks Generated → Sent → Received.
// The page does not generate documents and does not integrate with any
// e-signature provider; the LA produces the document via the existing
// report generator elsewhere, then comes here and clicks "Mark
// Generated" to record the attestation.

import { useMemo } from "react";
import { Badge, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useLinkApplicationDocument,
  useMarkApplicationGenerated,
  useMarkApplicationReceived,
  useMarkApplicationSent,
  usePreApprovalApplications,
  useUnlinkApplicationDocument,
  useUpdateApplicationNotes,
} from "@/hooks/deals/usePreApproval";
import {
  PREAPPROVAL_PERMISSIONS,
  type PreApprovalApplicationDto,
  type PreApprovalApplicationStatus,
  type PreApprovalPartyKind,
} from "@/types/deals";
import { PreApprovalAttestationRow, type MilestoneSpec } from "./PreApprovalAttestationRow";

interface Props {
  dealId: string;
}

const PARTY_ORDER: PreApprovalPartyKind[] = ["Borrower", "Seller"];

export function PreApprovalApplicationsSection({ dealId }: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(PREAPPROVAL_PERMISSIONS.ManageApplications);
  const query = usePreApprovalApplications(dealId);

  const markGenerated = useMarkApplicationGenerated(dealId);
  const markSent = useMarkApplicationSent(dealId);
  const markReceived = useMarkApplicationReceived(dealId);
  const updateNotes = useUpdateApplicationNotes(dealId);
  const unlinkDocument = useUnlinkApplicationDocument(dealId);
  // Reserved for the document-link picker; the picker UI itself is a
  // follow-up that opens the existing Documents tab in a side panel.
  const _link = useLinkApplicationDocument(dealId);
  void _link;

  // The two (Borrower + Seller) rows are self-healed on read by the API, so
  // they always exist for a deal in Pre-Approval or beyond.
  const rows = useMemo(() => {
    const byParty = new Map<PreApprovalPartyKind, PreApprovalApplicationDto>();
    for (const r of query.data ?? []) byParty.set(r.party, r);
    return PARTY_ORDER.map((p) => byParty.get(p)).filter(
      (r): r is PreApprovalApplicationDto => !!r,
    );
  }, [query.data]);

  const rollup = computeRollup(query.data ?? []);

  return (
    <Card>
      <div className="flex items-start justify-between flex-wrap gap-space-2 mb-space-3">
        <div>
          <div className="font-display font-bold text-h3 text-onyx">
            Section 1 — Applications
          </div>
          <p className="text-small text-gray-mid m-0">
            Attest that each pre-approval application has been Generated, Sent,
            and Received. Documents are produced via the report generator and
            stored in the Document Repository; this page records the LA's
            attestations only.
          </p>
        </div>
        <Badge status={rollup.tone}>{rollup.label}</Badge>
      </div>

      <div className="flex flex-col gap-space-3">
        {rows.map((row) => {
          const milestones: MilestoneSpec[] = [
            {
              key: "generated",
              label: "Mark Generated",
              isComplete: statusGte(row.status, "Generated"),
              enabled: row.status === "NotStarted",
              at: row.generatedAt,
              by: row.generatedByDisplayName,
              onClick: () => markGenerated.mutateAsync(row.id),
              pending: markGenerated.isPending && markGenerated.variables === row.id,
            },
            {
              key: "sent",
              label: "Mark Sent",
              isComplete: statusGte(row.status, "Sent"),
              enabled: row.status === "Generated",
              at: row.sentAt,
              by: row.sentByDisplayName,
              onClick: () => markSent.mutateAsync(row.id),
              pending: markSent.isPending && markSent.variables === row.id,
            },
            {
              key: "received",
              label: "Mark Received",
              isComplete: statusGte(row.status, "Received"),
              enabled: row.status === "Sent",
              at: row.receivedAt,
              by: row.receivedByDisplayName,
              onClick: () => markReceived.mutateAsync(row.id),
              pending: markReceived.isPending && markReceived.variables === row.id,
            },
          ];

          return (
            <PreApprovalAttestationRow
              key={row.id}
              dealId={dealId}
              party={row.party}
              statusLabel={statusLabel(row.status)}
              statusTone={statusTone(row.status)}
              milestones={milestones}
              notes={row.notes}
              onSaveNotes={async (notes) => {
                await updateNotes.mutateAsync({ id: row.id, body: { notes } });
              }}
              notesPending={updateNotes.isPending && updateNotes.variables?.id === row.id}
              linkedDocumentId={row.linkedDocumentId}
              onUnlinkDocument={async () => {
                await unlinkDocument.mutateAsync(row.id);
              }}
              unlinkPending={unlinkDocument.isPending && unlinkDocument.variables === row.id}
              canEdit={canEdit}
            />
          );
        })}
      </div>
    </Card>
  );
}

function statusGte(s: PreApprovalApplicationStatus, target: PreApprovalApplicationStatus) {
  const order: PreApprovalApplicationStatus[] = ["NotStarted", "Generated", "Sent", "Received"];
  return order.indexOf(s) >= order.indexOf(target);
}

function statusLabel(s: PreApprovalApplicationStatus): string {
  switch (s) {
    case "NotStarted": return "Not started";
    case "Generated": return "Generated";
    case "Sent": return "Sent";
    case "Received": return "Received";
  }
}

function statusTone(s: PreApprovalApplicationStatus): "draft" | "review" | "finalized" {
  switch (s) {
    case "NotStarted": return "draft";
    case "Received": return "finalized";
    default: return "review";
  }
}

function computeRollup(rows: PreApprovalApplicationDto[]): { label: string; tone: "draft" | "review" | "finalized" } {
  if (rows.length === 0) return { label: "Not started", tone: "draft" };
  if (rows.every((r) => r.status === "Received")) return { label: "Complete", tone: "finalized" };
  if (rows.every((r) => r.status === "NotStarted")) return { label: "Not started", tone: "draft" };
  return { label: "In progress", tone: "review" };
}
